// Mixer Brush tool — simulates wet paint mixing.
// Blends the brush color with existing canvas colors, creating a realistic paint mixing effect.

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import { stampLine, mergeBBox } from "../pixel/brush";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import type { BBox } from "../pixel/brush";
import type { KeyModifiers, PointerSample, RGB, Tool, ToolContext } from "./Tool";

export interface MixerBrushSettings {
  size: number;
  hardness: number;
  opacity: number;
  flow: number;
  wet: number;       // 0-1: how much paint is on the brush
  load: number;      // 0-1: how much paint the brush picks up from canvas
  mix: number;       // 0-1: how much brush color mixes with canvas color
}

export class MixerBrushTool implements Tool {
  readonly id = "mixer-brush";
  readonly shortcut = "B";

  settings: MixerBrushSettings = {
    size: 30,
    hardness: 0.5,
    opacity: 1,
    flow: 0.5,
    wet: 0.5,
    load: 0.5,
    mix: 0.5,
  };

  // Brush reservoir color (the "paint" on the brush)
  private brushColor: { r: number; g: number; b: number; a: number } = { r: 0, g: 0, b: 0, a: 0 };
  private brushHasPaint = false;

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
  } | null = null;

  onKeyDown(ctx: ToolContext, key: string, modifiers?: KeyModifiers): void {
    // Alt-click to sample color and load brush
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    // Alt+click samples color from canvas
    if (s.altKey) {
      const p = screenToDoc(ctx.viewport, s.x, s.y);
      this.sampleColor(layer, p.x - layer.x, p.y - layer.y);
      return;
    }

    // Load brush with foreground color if empty
    if (!this.brushHasPaint) {
      this.brushColor = { r: ctx.fgColor.r, g: ctx.fgColor.g, b: ctx.fgColor.b, a: 255 };
      this.brushHasPaint = true;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      lastX: docX,
      lastY: docY,
      bbox: null,
    };

    this.mix(ctx, docX, docY);
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x;
    const docY = p.y - this.active.layer.y;

    const step = 2;
    const dx = docX - this.active.lastX;
    const dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastX + dx * t;
      const cy = this.active.lastY + dy * t;
      this.mix(ctx, cx, cy);
    }

    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }

    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  private sampleColor(layer: RasterLayer, docX: number, docY: number): void {
    const px = Math.floor(docX);
    const py = Math.floor(docY);
    const w = layer.pixels.width;
    if (px < 0 || py < 0 || px >= w || py >= layer.pixels.height) return;

    const idx = (py * w + px) * 4;
    this.brushColor = {
      r: layer.pixels.data[idx]!,
      g: layer.pixels.data[idx + 1]!,
      b: layer.pixels.data[idx + 2]!,
      a: layer.pixels.data[idx + 3]!,
    };
    this.brushHasPaint = true;
  }

  private mix(ctx: ToolContext, docX: number, docY: number): void {
    if (!this.active || !this.brushHasPaint) return;

    const { layer } = this.active;
    const data = layer.pixels.data;
    const w = layer.pixels.width;
    const h = layer.pixels.height;
    const radius = this.settings.size / 2;
    const r2 = radius * radius;
    const wet = this.settings.wet;
    const load = this.settings.load;
    const mix = this.settings.mix;
    const flow = this.settings.flow;

    const minX = Math.max(0, Math.floor(docX - radius));
    const minY = Math.max(0, Math.floor(docY - radius));
    const maxX = Math.min(w - 1, Math.ceil(docX + radius));
    const maxY = Math.min(h - 1, Math.ceil(docY + radius));

    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - docY;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - docX;
        if (dx * dx + dy2 >= r2) continue;

        const idx = (py * w + px) * 4;
        const canvasA = data[idx + 3]!;
        if (canvasA === 0 && !this.brushHasPaint) continue;

        // Soft-edge falloff
        const dist = Math.sqrt(dx * dx + dy2) / radius;
        const falloff = dist >= 1 ? 0 : dist > this.settings.hardness
          ? (1 - dist) / (1 - this.settings.hardness || 1e-6)
          : 1;

        const strength = falloff * flow;
        if (strength <= 0) continue;

        // Get canvas color (or transparent)
        const canvasR = canvasA > 0 ? data[idx]! : this.brushColor.r;
        const canvasG = canvasA > 0 ? data[idx + 1]! : this.brushColor.g;
        const canvasB = canvasA > 0 ? data[idx + 2]! : this.brushColor.b;

        // Mix brush color with canvas color
        const mixedR = Math.round(this.brushColor.r * mix + canvasR * (1 - mix));
        const mixedG = Math.round(this.brushColor.g * mix + canvasG * (1 - mix));
        const mixedB = Math.round(this.brushColor.b * mix + canvasB * (1 - mix));

        // Blend mixed color onto canvas
        const blendAmount = strength * wet;
        data[idx] = Math.round(data[idx]! * (1 - blendAmount) + mixedR * blendAmount);
        data[idx + 1] = Math.round(data[idx + 1]! * (1 - blendAmount) + mixedG * blendAmount);
        data[idx + 2] = Math.round(data[idx + 2]! * (1 - blendAmount) + mixedB * blendAmount);
        // Increase alpha where we paint
        const newAlpha = Math.min(255, Math.round(canvasA + (255 - canvasA) * blendAmount * load));
        data[idx + 3] = newAlpha;

        // Update brush reservoir (pick up some canvas color)
        const pickupAmount = strength * load * 0.1;
        if (canvasA > 0) {
          this.brushColor.r = Math.round(this.brushColor.r * (1 - pickupAmount) + canvasR * pickupAmount);
          this.brushColor.g = Math.round(this.brushColor.g * (1 - pickupAmount) + canvasG * pickupAmount);
          this.brushColor.b = Math.round(this.brushColor.b * (1 - pickupAmount) + canvasB * pickupAmount);
        }

        if (px < touchX0) touchX0 = px;
        if (py < touchY0) touchY0 = py;
        if (px > touchX1) touchX1 = px;
        if (py > touchY1) touchY1 = py;
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    layer.pixels.markDirty();
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}
